import { useCallback, useState } from "react";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import { Check, Copy } from "lucide-react";
import { toast } from "sonner";

const DOTS = ["#ff5f56", "#ffbd2e", "#27c93f"];

/* Dark, editor-style code block. Reads a raw string, applies Prism syntax
   highlighting and preserves whitespace/indentation/line breaks exactly so
   the "Copier le code" button yields a paste-ready copy. */
export function CodeBlock({ code = "", language = "text", filename }) {
  const [copied, setCopied] = useState(false);

  const copy = useCallback(async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(code);
      } else {
        const ta = document.createElement("textarea");
        ta.value = code;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) {
      toast.error("Copie bloquée par le navigateur — sélectionnez le code manuellement");
    }
  }, [code]);

  const lineCount = code.split("\n").length;

  return (
    <div
      data-testid="code-block"
      className="my-3 overflow-hidden rounded-xl border border-white/10 bg-[#0b0f17] shadow-[0_10px_40px_-18px_rgba(0,0,0,0.85)]"
    >
      <div className="flex items-center justify-between gap-3 border-b border-white/8 bg-[#11161f] px-3.5 py-2">
        <div className="flex min-w-0 items-center gap-1.5">
          {DOTS.map((c) => (
            <span key={c} className="h-3 w-3 rounded-full" style={{ background: c }} />
          ))}
          <span className="ml-3 truncate font-mono text-[11px] tracking-wide text-slate-400">
            {filename || language}
          </span>
        </div>
        <button
          type="button"
          data-testid="code-copy-button"
          onClick={copy}
          className={`flex shrink-0 items-center gap-1.5 rounded-md border px-2.5 py-1 font-mono text-[11px] transition-colors ${
            copied
              ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
              : "border-white/10 bg-white/5 text-slate-300 hover:bg-white/10 hover:text-white"
          }`}
        >
          {copied ? (
            <>
              <Check className="h-3.5 w-3.5" /> Copié
            </>
          ) : (
            <>
              <Copy className="h-3.5 w-3.5" /> Copier le code
            </>
          )}
        </button>
      </div>
      <div className="max-h-[520px] overflow-auto">
        <SyntaxHighlighter
          language={language}
          style={oneDark}
          showLineNumbers={lineCount > 4}
          lineNumberStyle={{ color: "#3a4658", minWidth: "2.4em", paddingRight: "1em", userSelect: "none" }}
          customStyle={{
            margin: 0,
            background: "transparent",
            padding: "16px",
            fontSize: "13px",
            lineHeight: "1.6",
          }}
          codeTagProps={{
            style: {
              fontFamily: "'JetBrains Mono','Fira Code',ui-monospace,SFMono-Regular,Menlo,monospace",
              whiteSpace: "pre",
            },
          }}
        >
          {code}
        </SyntaxHighlighter>
      </div>
    </div>
  );
}
